/**
 * Demo API Wrapper
 *
 * Intercepts API calls in demo mode (Anonymous/Onboarding) and returns mock data
 * In subscribed mode, passes through to real API
 * Always passes through assessment-related endpoints
 */

import { IS_STATIC_DEMO } from './config';
import {
  DEMO_CLIENT_ID,
  PROJECT_AI_TRANSFORM_ID, PROJECT_CLOUD_MIGRATION_ID,
  PROJECT_DATA_LAKE_ID, PROJECT_SECURITY_ID,
  TICKET_1_ID, TICKET_2_ID, TICKET_3_ID
} from './mockData';
import {
  getDemoNotifications,
  getDemoUnreadCount,
  markDemoNotificationRead,
  markAllDemoNotificationsRead,
  deleteDemoNotification,
} from './demoNotifications';
import { MockDataStore } from './demoModeManager';
import { ApiCallOptions } from '../types/api';

// Endpoints that should NEVER be intercepted (always use real API)
// In static demo mode, NOTHING passes through
const PASSTHROUGH_ENDPOINTS = [
  '/assessments',
  '/assessments/',
  '/assessment-results',
  '/assessment-results/',
  '/auth/me',
  '/auth/',
  '/clients/upgrade-to-trial',
];

// Helper to check if endpoint should pass through to real API
const shouldPassThrough = (endpoint: string): boolean => {
  if (IS_STATIC_DEMO) return false; // Never pass through in static demo
  return PASSTHROUGH_ENDPOINTS.some(pattern => endpoint.startsWith(pattern));
};

interface ParsedEndpoint {
  resource: string;
  id: string | null;
  subResource: string | null;
  subId: string | null;
  method: string;
  fullPath: string;
}

// Helper to parse endpoint and extract resource info
const parseEndpoint = (endpoint: string, method: string): ParsedEndpoint => {
  const cleanEndpoint = endpoint.split('?')[0]; // Remove query params
  const parts = cleanEndpoint.split('/').filter(Boolean);

  return {
    resource: parts[0], // e.g., 'projects', 'clients', 'timesheets'
    id: parts[1] || null,
    subResource: parts[2] || null,
    subId: parts[3] || null,
    method: method || 'GET',
    fullPath: cleanEndpoint
  };
};

// Map API resource names to mock data collections
const RESOURCE_MAP: Record<string, string> = {
  'projects': 'projects',
  'workloads': 'workloads',
  'clients': 'clients',
  'timesheets': 'timesheets',
  'milestones': 'milestones',
  'support': 'supportTickets',
  'support-tickets': 'supportTickets',
  'finops': 'costs',
  'budgets': 'budgets',
  'costs': 'costs',
  'tagging': 'tagging',
  'guardrails': 'guardrails',
  'datasets': 'datasets',
  'data-quality': 'dataQualityRuns',
  'genai': 'genaiTraces',
  'genaiops': 'genaiTraces',
  'traces': 'genaiTraces',
  'prompts': 'prompts',
  'evaluations': 'evaluations',
  'agents': 'agentRuns',
  'agent-runs': 'agentRuns',
  'agent-tools': 'agentTools',
  'dashboard': 'dashboardStats',
  'dashboards': 'dashboardStats',
  'auth': 'userAuth',
  'me': 'userAuth',
  'users': 'users',
  'employees': 'employees',
  'permissions': 'userAuth',
  'roles': 'userAuth',
  'modules': 'userAuth',
  'finance': 'costs',
  'communications': 'supportTickets',
  'notifications': 'supportTickets',
  'integrations': 'integrations',
  'funding': 'funding',
  'dashboard-templates': 'dashboardTemplates',
  'aws': 'awsEnvironments',
  'tasks': 'tasks',
  'assessments': 'assessments',
  'assessment-results': 'assessments',
  'aws-users': 'clients',
  'notification-catalog': 'notifications'
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic API wrapper returns dynamic response types
export type RealApiCall = (endpoint: string, options?: ApiCallOptions) => Promise<any>;

/**
 * Create a demo API wrapper that intercepts calls in demo mode
 */
export const createDemoApiWrapper = (
  mockDataStore: MockDataStore,
  isDemoMode: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic API wrapper returns dynamic response types
): ((realApiCall: RealApiCall, endpoint: string, options?: ApiCallOptions) => Promise<any>) => {
  /**
   * Wrapper for apiCall function
   * In demo mode: returns mock data
   * In subscribed mode or for assessments: passes through to real API
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return async (realApiCall: RealApiCall, endpoint: string, options: ApiCallOptions = {}): Promise<any> => {
    // Always pass through critical endpoints to real API
    if (shouldPassThrough(endpoint)) {
      return realApiCall(endpoint, options);
    }

    // If not in demo mode, pass through to real API
    if (!isDemoMode || !mockDataStore.isReady) {
      return realApiCall(endpoint, options);
    }

    // Handle demo mode - return mock data
    return handleMockRequest(mockDataStore, endpoint, options);
  };
};

/**
 * Handle mock API requests and return appropriate mock data
 */
const handleMockRequest = async (
  mockDataStore: MockDataStore,
  endpoint: string,
  options: ApiCallOptions = {}
): Promise<unknown> => {
  const method = options.method || 'GET';
  const parsed = parseEndpoint(endpoint, method);
  const collection = RESOURCE_MAP[parsed.resource];

  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));

  // --- Demo notifications (all methods) ---
  if (parsed.resource === 'notifications') {
    // Preferences / subscriptions — keep returning empty
    if (endpoint.includes('preferences')) {
      return { preferences: [], default_types: [] };
    }
    if (endpoint.includes('subscriptions')) {
      return [];
    }

    // GET /notifications/unread-count
    if (method === 'GET' && endpoint.includes('unread-count')) {
      return { unread_count: getDemoUnreadCount() };
    }

    // PUT /notifications/{id}/read
    if (method === 'PUT' && parsed.id && endpoint.includes('/read')) {
      // read-all (no specific id path, but endpoint matches "read-all")
      if (endpoint.includes('read-all')) {
        markAllDemoNotificationsRead();
        return { marked_count: 0 };
      }
      markDemoNotificationRead(parsed.id);
      return { success: true };
    }

    // DELETE /notifications/{id}
    if (method === 'DELETE' && parsed.id) {
      deleteDemoNotification(parsed.id);
      return null;
    }

    // GET /notifications (list)
    if (method === 'GET') {
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const filter = {
        unreadOnly: urlParams.get('unread_only') === 'true',
        category: urlParams.get('category') || undefined,
      };
      return getDemoNotifications(filter);
    }

    // Fallback for any other notification method
    return { notifications: [], total: 0, unread_count: 0 };
  }

  // --- SSO configurations (demo mode mock) ---
  if (parsed.resource === 'sso') {
    if (method === 'GET' && endpoint.includes('/configurations')) {
      return [];
    }
    if (method === 'POST') {
      return { id: 'demo-sso-config', enforcement_mode: 'disabled', scim_enabled: false, is_active: true };
    }
    return null;
  }

  try {
    switch (method) {
      case 'GET':
        return handleGet(mockDataStore, parsed, collection, endpoint);

      case 'POST':
        return handlePost(mockDataStore, parsed, collection, options);

      case 'PUT':
      case 'PATCH':
        return handleUpdate(mockDataStore, parsed, collection, options);

      case 'DELETE':
        return handleDelete(mockDataStore, parsed, collection);

      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  } catch (error) {
    console.error('Mock API error:', error);
    throw error;
  }
};

/**
 * Mock assessment data for static demo mode
 */
const MOCK_ASSESSMENT_ID_1 = 'as000000-0000-4000-a000-000000000001';
const MOCK_ASSESSMENT_ID_2 = 'as000000-0000-4000-a000-000000000002';
const MOCK_ASSESSMENT_ID_3 = 'as000000-0000-4000-a000-000000000003';

const mockAssessments = [
  {
    id: MOCK_ASSESSMENT_ID_2,
    name: 'TechCorp Q2 Reassessment',
    status: 'draft',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: null,
    client_name: 'TechCorp Industries',
    client_id: 'c0000000-0000-4000-a000-000000000001',
    owner_email: 'client@example.com',
    owner_name: 'Client User',
    overall_score: null,
    score: null,
    readiness_level: null,
  },
  {
    id: MOCK_ASSESSMENT_ID_1,
    name: 'TechCorp AI Strategy Assessment',
    status: 'completed',
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    client_name: 'TechCorp Industries',
    client_id: 'c0000000-0000-4000-a000-000000000001',
    owner_email: 'client@example.com',
    owner_name: 'Client User',
    overall_score: 50,
    score: 50,
    readiness_level: 'Moderate',
  },
  {
    id: MOCK_ASSESSMENT_ID_3,
    name: 'TechCorp Initial Assessment',
    status: 'completed',
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 59 * 24 * 60 * 60 * 1000).toISOString(),
    completed_at: new Date(Date.now() - 59 * 24 * 60 * 60 * 1000).toISOString(),
    client_name: 'TechCorp Industries',
    client_id: 'c0000000-0000-4000-a000-000000000001',
    owner_email: 'client@example.com',
    owner_name: 'Client User',
    overall_score: 35,
    score: 35,
    readiness_level: 'Early Stage',
  },
];

const mockLatestCompletedResponses = {
  responses: {
    cloud_provider: 'AWS',
    success_vision: 'Improve customer experience with AI-powered recommendations',
    ai_implementation_stage: 'Early implementation - first AI/ML project in development',
    user_persona: 'both',
  }
};

// NOTE: mockAssessmentResultsById is defined after mockAssessmentResults
// so it can spread the full data (roadmap, recommendations, scorecard, etc.)

const mockAssessmentResults = {
  id: 'ar000000-0000-4000-a000-000000000001',
  assessment_id: MOCK_ASSESSMENT_ID_1,
  overall_score: 50,
  readiness_level: 'Moderate',
  category_scores: {
    business_objectives: 60,
    data_readiness: 30,
    technical_infrastructure: 55,
    organizational_readiness: 65,
    governance_compliance: 40,
    ai_ml_implementation: 50,
  },
  roadmap: [
    {
      phase: 'Phase 1: AI Foundations & Security Hardening (0-3 months)',
      items: [
        'Implement AWS Secrets Manager for centralized secrets storage and rotation',
        'Establish baseline security controls using AWS Security Hub CIS Benchmark',
        'Define and prioritize AI/ML use cases with business stakeholders',
        'Enable AWS CloudTrail for comprehensive audit logging across AI services',
        'Create multi-account structure with AWS Control Tower for AI workload isolation',
      ]
    },
    {
      phase: 'Phase 2: Data & Infrastructure Readiness (3-6 months)',
      items: [
        'Implement Amazon DataZone for data cataloging and governance',
        'Set up AWS Glue Data Quality for automated data validation before AI ingestion',
        'Configure Amazon S3 Cross-Region Replication for disaster recovery of training data',
        'Establish IAM least-privilege policies for SageMaker and Bedrock services',
        'Create data residency controls with AWS Organizations SCPs for compliance',
      ]
    },
    {
      phase: 'Phase 3: AI/ML Service Implementation (6-9 months)',
      items: [
        'Deploy Amazon Bedrock for foundational GenAI capabilities and prototyping',
        'Implement Amazon Bedrock Guardrails for content safety and PII filtering',
        'Set up Amazon Bedrock Knowledge Bases for RAG over enterprise documents',
        'Establish prompt versioning and management in CodeCommit',
        'Configure automated model evaluations with Bedrock Model Evaluation',
      ]
    },
    {
      phase: 'Phase 4: Operational Excellence & Optimization (9-12 months)',
      items: [
        'Implement MLOps pipeline with SageMaker Pipelines for automated training and deployment',
        'Enable model monitoring with SageMaker Model Monitor for data drift detection',
        'Set up automated rollback with AWS CodeDeploy for safe model releases',
        'Configure shadow testing for model changes before production promotion',
        'Implement multi-model routing in Bedrock for A/B testing and cost optimization',
      ]
    },
  ],
  azure_services: [],
  aws_services: [
    { name: 'AWS Secrets Manager', description: 'Central secrets storage and automatic rotation for AI service credentials', relevance: 'High', useCase: 'Secure API keys and database credentials for ML training pipelines and inference endpoints' },
    { name: 'Amazon Bedrock', description: 'Core GenAI capabilities with managed foundation models for prototyping and production', relevance: 'High', useCase: 'Deploy foundation models for customer support automation, document summarization, and code generation' },
    { name: 'AWS Control Tower', description: 'Multi-account landing zone structure for isolated AI/ML workload environments', relevance: 'High', useCase: 'Establish governed multi-account structure separating AI dev, staging, and production workloads' },
    { name: 'Amazon DataZone', description: 'Unified data catalog and governance platform for AI-ready data management', relevance: 'Critical', useCase: 'Catalog enterprise datasets and enforce access policies for ML training data discovery' },
    { name: 'AWS Glue Data Quality', description: 'Automated data validation to ensure integrity of AI model training datasets', relevance: 'High', useCase: 'Validate data completeness and accuracy before ingestion into SageMaker training jobs' },
    { name: 'Amazon CloudWatch', description: 'Centralized logging and monitoring for AI inference endpoints and pipelines', relevance: 'High', useCase: 'Monitor Bedrock invocation latency, token usage, and SageMaker endpoint health in real time' },
    { name: 'AWS Security Hub', description: 'CIS benchmark compliance checks and security posture management for AI infrastructure', relevance: 'High', useCase: 'Continuously audit security posture of VPCs, IAM roles, and S3 buckets hosting AI workloads' },
    { name: 'Amazon Bedrock Guardrails', description: 'Content safety filters and PII detection to prevent harmful AI outputs', relevance: 'Critical', useCase: 'Block PII leakage and enforce content policies across all GenAI-powered customer-facing applications' },
  ],
  recommendations: [
    { title: 'Implement Centralized Secrets Management', description: 'Deploy AWS Secrets Manager to eliminate hardcoded credentials scattered across AI service configurations and environment variables', priority: 'Critical', category: 'Security Hardening', effort: 'Medium', timeline: '2-4 weeks', service: 'AWS Secrets Manager' },
    { title: 'Define Generative AI Use Cases', description: 'Document specific GenAI use cases tied to measurable business outcomes (cost reduction, revenue growth) before expanding beyond prototypes', priority: 'High', category: 'AI/ML Use Case Identification', effort: 'Low', timeline: '2-3 weeks', service: 'Amazon Bedrock' },
    { title: 'Establish Centralized Logging', description: 'Implement CloudWatch and CloudTrail to gain visibility into AI model invocations, data access patterns, and infrastructure health', priority: 'High', category: 'Observability', effort: 'Medium', timeline: '3-4 weeks', service: 'Amazon CloudWatch' },
    { title: 'Implement Data Catalog', description: 'Deploy Amazon DataZone as a unified data catalog to break down data silos that block AI training pipeline development and model reproducibility', priority: 'Critical', category: 'Data Governance', effort: 'High', timeline: '6-8 weeks', service: 'Amazon DataZone' },
    { title: 'Apply Security Benchmarks', description: 'Enable AWS Security Hub with CIS Benchmark to continuously audit the security posture of infrastructure supporting AI workloads', priority: 'High', category: 'Security Hardening', effort: 'Medium', timeline: '2-3 weeks', service: 'AWS Security Hub' },
    { title: 'Secure Network Connectivity', description: 'Implement VPC endpoints for private connectivity to Bedrock and SageMaker, eliminating public internet exposure for AI service traffic', priority: 'High', category: 'Network Security', effort: 'Medium', timeline: '2-4 weeks', service: 'Amazon VPC' },
    { title: 'Implement KMS Encryption', description: 'Enable KMS encryption-at-rest for all S3 buckets containing training data, model artifacts, and inference logs to meet compliance requirements', priority: 'High', category: 'Encryption Implementation', effort: 'Low', timeline: '1-2 weeks', service: 'AWS KMS' },
    { title: 'Establish Model Versioning', description: 'Set up prompt and model versioning in CodeCommit to enable safe rollbacks, audit trails, and reproducible AI experiments', priority: 'High', category: 'Model Governance', effort: 'Medium', timeline: '3-4 weeks', service: 'AWS CodeCommit' },
  ],
  scorecard: [
    { category: 'Identity Management', description: 'Enterprise identity and access management maturity', maturityLevel: 'Advanced', priority: 'High', applicableServices: 'IAM Identity Center, AWS SSO', notes: 'Enterprise SSO implemented with IAM Identity Center; strong identity foundation for AI service access' },
    { category: 'Secrets Management', description: 'Secure storage and rotation of credentials and API keys', maturityLevel: 'Minimal', priority: 'Critical', applicableServices: 'AWS Secrets Manager, AWS KMS', notes: 'No secrets management solution in place; credentials scattered across environment variables' },
    { category: 'Disaster Recovery', description: 'Business continuity and failover readiness for AI workloads', maturityLevel: 'Basic', priority: 'High', applicableServices: 'S3 CRR, AWS Backup', notes: 'DR processes documented but lack technical implementation for ML training data and model artifacts' },
    { category: 'ML Platform Selection', description: 'Selection and implementation of core AI/ML platforms', maturityLevel: 'Intermediate', priority: 'High', applicableServices: 'Amazon SageMaker, Amazon Bedrock', notes: 'Initial Bedrock prototypes running; needs guidance on SageMaker vs Bedrock selection for production' },
    { category: 'Infrastructure Automation', description: 'Infrastructure as Code and CI/CD pipeline maturity', maturityLevel: 'Minimal', priority: 'Medium', applicableServices: 'AWS CDK, CloudFormation, CodePipeline', notes: 'No IaC or CI/CD practices for infrastructure; manual deployments create inconsistency risk' },
    { category: 'Observability', description: 'Centralized logging, monitoring, and alerting capabilities', maturityLevel: 'Minimal', priority: 'High', applicableServices: 'Amazon CloudWatch, AWS X-Ray', notes: 'No centralized logging or monitoring; AI model performance blind spots' },
    { category: 'Data Governance', description: 'Data catalog, lineage, and access control framework', maturityLevel: 'Minimal', priority: 'Critical', applicableServices: 'Amazon DataZone, AWS Lake Formation', notes: 'No data catalog or governance framework; data silos block AI training pipeline development' },
    { category: 'Compliance Readiness', description: 'Regulatory compliance controls for AI workloads', maturityLevel: 'Basic', priority: 'High', applicableServices: 'AWS Config, AWS Audit Manager', notes: 'No AI-specific compliance controls; GDPR and industry regulations not yet mapped to controls' },
    { category: 'Cost Management', description: 'Cloud cost tracking, budgets, and optimization for AI', maturityLevel: 'Minimal', priority: 'Medium', applicableServices: 'AWS Cost Explorer, AWS Budgets', notes: 'No cost tracking or alerts for AI/ML workloads; risk of uncontrolled Bedrock and SageMaker spend' },
    { category: 'Security Hardening', description: 'Security benchmarks and vulnerability management', maturityLevel: 'Basic', priority: 'High', applicableServices: 'AWS Security Hub, Amazon GuardDuty', notes: 'No explicit security benchmarks applied; AI infrastructure lacks CIS compliance validation' },
    { category: 'AI/ML Use Case Identification', description: 'Defined and prioritized AI use cases with business alignment', maturityLevel: 'Minimal', priority: 'Critical', applicableServices: 'Amazon Bedrock, Amazon SageMaker', notes: 'No specific GenAI or ML use cases formally defined; prototypes lack business case documentation' },
    { category: 'Data Quality Management', description: 'Automated data quality checks and validation pipelines', maturityLevel: 'Minimal', priority: 'High', applicableServices: 'AWS Glue Data Quality, Glue DataBrew', notes: 'No data quality checks before AI model training; risk of model drift from unchecked data issues' },
  ],
};

const mockAssessmentResultsById: Record<string, unknown> = {
  [MOCK_ASSESSMENT_ID_1]: {
    ...mockAssessmentResults,
    id: 'ar000000-0000-4000-a000-000000000001',
    assessment_id: MOCK_ASSESSMENT_ID_1,
    overall_score: 50,
    readiness_level: 'Moderate',
    category_scores: {
      business_objectives: 60,
      data_readiness: 30,
      technical_infrastructure: 55,
      organizational_readiness: 65,
      governance_compliance: 40,
      ai_ml_implementation: 50,
    },
  },
  [MOCK_ASSESSMENT_ID_3]: {
    ...mockAssessmentResults,
    id: 'ar000000-0000-4000-a000-000000000003',
    assessment_id: MOCK_ASSESSMENT_ID_3,
    overall_score: 35,
    readiness_level: 'Early Stage',
    category_scores: {
      business_objectives: 40,
      data_readiness: 20,
      technical_infrastructure: 40,
      organizational_readiness: 45,
      governance_compliance: 30,
      ai_ml_implementation: 35,
    },
  },
};

/**
 * Mock health dashboard data
 */
const getHealthDashboardMockData = (subEndpoint: string): unknown => {
  const CLIENT_ID = DEMO_CLIENT_ID;
  switch (subEndpoint) {
    case 'delivery-metrics':
      return {
        on_time_rate: 87.5,
        total_projects: 4,
        on_track: 3,
        at_risk: 1,
        behind_schedule: 0,
        milestone_completion_rate: 62,
        avg_days_behind: 3.2,
      };
    case 'budget-metrics':
      return {
        total_budget: 215000,
        total_spent: 69000,
        utilization_pct: 61.5,
        over_budget_projects: 0,
        overrun_count: 0,
        overrun_amount: 0,
        spend_by_client: [
          { client_name: 'TechCorp Industries', total_spent: 69000 },
        ],
        alerts: [
          { project_id: PROJECT_DATA_LAKE_ID, project_name: 'Data Lake Implementation', client_id: CLIENT_ID, client_name: 'TechCorp Industries', utilization_pct: 82 },
        ],
      };
    case 'support-metrics':
      return {
        open_tickets: 3,
        pending_count: 3,
        avg_resolution_hours: 18.5,
        sla_compliance: 94.2,
        open_requests: [
          { id: TICKET_1_ID, subject: 'API Gateway timeout issues', client_id: CLIENT_ID, client_name: 'TechCorp Industries', priority: 'High' },
          { id: TICKET_2_ID, subject: 'Request for additional S3 bucket', client_id: CLIENT_ID, client_name: 'TechCorp Industries', priority: 'Medium' },
          { id: TICKET_3_ID, subject: 'IAM permissions for new team member', client_id: CLIENT_ID, client_name: 'TechCorp Industries', priority: 'Low' },
        ],
      };
    case 'projects-at-risk':
      return {
        count: 1,
        critical: [],
        warning: [
          { id: PROJECT_DATA_LAKE_ID, project_id: PROJECT_DATA_LAKE_ID, name: 'Data Lake Implementation', client_id: CLIENT_ID, client_name: 'TechCorp Industries', days_behind: 5, status: 'at_risk' },
        ],
      };
    case 'project-health-map':
      return {
        projects: [
          { name: 'AI Transformation Initiative', health_status: 'green', client_name: 'TechCorp Industries', client_id: CLIENT_ID, project_id: PROJECT_AI_TRANSFORM_ID, infrastructure_budget: 15000, infrastructure_spent: 10500, progress: 33, days_elapsed: 120, days_total: 365, time_elapsed_pct: 33 },
          { name: 'Cloud Migration - Phase 2', health_status: 'green', client_name: 'TechCorp Industries', client_id: CLIENT_ID, project_id: PROJECT_CLOUD_MIGRATION_ID, infrastructure_budget: 8000, infrastructure_spent: 5200, progress: 50, days_elapsed: 90, days_total: 180, time_elapsed_pct: 50 },
          { name: 'Data Lake Implementation', health_status: 'yellow', client_name: 'TechCorp Industries', client_id: CLIENT_ID, project_id: PROJECT_DATA_LAKE_ID, infrastructure_budget: 6000, infrastructure_spent: 0, progress: 65, days_elapsed: 150, days_total: 200, time_elapsed_pct: 75 },
          { name: 'Security & Compliance Upgrade', health_status: 'green', client_name: 'TechCorp Industries', client_id: CLIENT_ID, project_id: PROJECT_SECURITY_ID, infrastructure_budget: 5000, infrastructure_spent: 4800, progress: 25, days_elapsed: 45, days_total: 120, time_elapsed_pct: 38 },
        ],
      };
    case 'funding-by-program':
      return {
        programs: [
          { name: 'AWS Partner Funding', total_amount: 10000, used_amount: 4200, status: 'Active' },
          { name: 'AWS Migration Acceleration', total_amount: 7500, used_amount: 0, status: 'Pending' },
        ],
      };
    case 'credits-by-client':
      return {
        clients: [
          { client_name: 'TechCorp Industries', total_credits: 10000, used_credits: 4200 },
        ],
      };
    case 'credit-expiration':
      return {
        timeline: [
          { month: '2026-03', amount: 0 },
          { month: '2026-06', amount: 3500 },
          { month: '2026-09', amount: 0 },
          { month: '2026-12', amount: 7500 },
        ],
      };
    case 'funding-requests':
      return {
        requests: [
          { id: 'app-001', name: 'AI Transformation Credits', client_name: 'TechCorp Industries', program_name: 'AWS Partner Funding', status: 'APPROVED', requested_amount: 10000 },
          { id: 'app-002', name: 'Cloud Migration Support', client_name: 'TechCorp Industries', program_name: 'AWS Migration Acceleration', status: 'PENDING_SUBMISSION', requested_amount: 7500 },
        ],
      };
    default:
      return null;
  }
};

/**
 * Mock AWS Insights data
 */
const getAWSInsightsMockData = (subEndpoint: string): unknown => {
  switch (subEndpoint) {
    case 'project-health-map':
      return [
        { client_name: 'TechCorp Industries', project_name: 'AI Transformation', health_status: 'on_track', budget_utilization: 55 },
        { client_name: 'TechCorp Industries', project_name: 'Cloud Migration', health_status: 'on_track', budget_utilization: 42 },
        { client_name: 'TechCorp Industries', project_name: 'Data Lake Implementation', health_status: 'at_risk', budget_utilization: 78 },
        { client_name: 'TechCorp Industries', project_name: 'Security Hardening', health_status: 'on_track', budget_utilization: 35 },
      ];
    case 'infrastructure-spend':
      return { total_spend: 29550, monthly_trend: [2100, 2200, 2350, 2600, 2900, 3480], forecast: 42000 };
    case 'funding-summary':
      return { total_approved: 10000, total_pending: 7500, total_used: 4200, programs_count: 2 };
    case 'credits-overview':
      return { total_credits: 10000, used_credits: 4200, remaining_credits: 5800, active_codes: 2 };
    case 'funding-by-program':
      return [
        { program_name: 'AWS Partner Funding', total_amount: 10000, used_amount: 4200, status: 'Active' },
        { program_name: 'AWS Migration Acceleration', total_amount: 7500, used_amount: 0, status: 'Pending' },
      ];
    case 'funding-timeline':
      return [
        { month: '2025-01', approved: 10000, used: 1200 },
        { month: '2025-02', approved: 0, used: 1500 },
        { month: '2025-03', approved: 0, used: 1500 },
      ];
    case 'credits-by-client':
      return [{ client_name: 'TechCorp Industries', total_credits: 10000, used_credits: 4200, remaining: 5800 }];
    case 'projects-at-risk':
      return { count: 1, projects: [{ name: 'Data Lake Implementation', health: 'at_risk', client_name: 'TechCorp Industries', budget_utilization: 78 }] };
    default:
      return null;
  }
};

/**
 * Handle GET requests
 */
const handleGet = (
  mockDataStore: MockDataStore,
  parsed: ParsedEndpoint,
  collection: string,
  endpoint: string
): unknown => {
  // Special handling for auth endpoints
  if (endpoint === '/auth/me' || endpoint.startsWith('/auth/me')) {
    // In static demo, return user from localStorage
    if (IS_STATIC_DEMO) {
      const stored = localStorage.getItem('user');
      if (stored) {
        try { return JSON.parse(stored); } catch { /* fall through */ }
      }
    }
    return mockDataStore.get('userAuth');
  }

  if (parsed.resource === 'auth' && parsed.id === 'me') {
    if (IS_STATIC_DEMO) {
      const stored = localStorage.getItem('user');
      if (stored) {
        try { return JSON.parse(stored); } catch { /* fall through */ }
      }
    }
    return mockDataStore.get('userAuth');
  }

  if (parsed.resource === 'me') {
    return mockDataStore.get('userAuth');
  }

  // Handle assessment endpoints (static demo mode)
  if (parsed.resource === 'assessments') {
    if (parsed.id === 'latest-completed') {
      // GET /assessments/latest-completed
      return mockLatestCompletedResponses;
    }
    if (parsed.id === 'draft' && !parsed.subResource) {
      // POST /assessments/draft is handled in handlePost
      return [];
    }
    if (parsed.id && parsed.subResource === 'results') {
      // GET /assessments/:id/results — return per-assessment mock if available
      return mockAssessmentResultsById[parsed.id] || mockAssessmentResults;
    }
    if (parsed.id && parsed.subResource === 'draft') {
      // GET /assessments/:id/draft - load from localStorage
      const stored = localStorage.getItem(`demo_draft_${parsed.id}`);
      return stored ? { responses: JSON.parse(stored) } : { responses: {} };
    }
    if (parsed.id && !parsed.subResource) {
      // GET /assessments/:id
      return mockAssessments.find(a => a.id === parsed.id) || mockAssessments[0];
    }
    // GET /assessments (list)
    return mockAssessments;
  }

  // Handle assessment-results endpoints
  if (parsed.resource === 'assessment-results') {
    if (parsed.id) {
      return mockAssessmentResults;
    }
    return [mockAssessmentResults];
  }

  // Handle aws-users/me/accessible-clients
  if (parsed.resource === 'aws-users') {
    if (parsed.id === 'me' && parsed.subResource === 'accessible-clients') {
      return mockDataStore.get('clients') || [
        { id: 'c0000000-0000-4000-a000-000000000001', name: 'TechCorp Industries', subscription_plan: 'paid' }
      ];
    }
    return [];
  }

  // Handle Health Dashboard endpoints (/dashboard/health/*)
  if (parsed.resource === 'dashboard' && parsed.id === 'health' && parsed.subResource) {
    return getHealthDashboardMockData(parsed.subResource);
  }

  // Handle AWS Insights endpoints (/dashboard/aws-insights/*)
  if (parsed.resource === 'dashboard' && parsed.id === 'aws-insights' && parsed.subResource) {
    return getAWSInsightsMockData(parsed.subResource);
  }

  // Handle finops/manual-credits and manual-credits-summary
  if (parsed.resource === 'finops') {
    if (parsed.id === 'manual-credits-summary') {
      return {
        total_credits: 10000,
        used_credits: 4200,
        remaining_credits: 5800,
        active_credit_codes: 2,
        expired_credit_codes: 1,
      };
    }
    if (parsed.id === 'manual-credits') {
      return [
        { id: 'mc-001', description: 'Q1 2025 AI Training Credits', amount: 6500, used: 4200, status: 'active', created_at: '2025-01-15T10:00:00Z' },
        { id: 'mc-002', description: 'Q2 2025 Infrastructure Credits', amount: 3500, used: 0, status: 'active', created_at: '2025-04-01T10:00:00Z' },
      ];
    }
  }

  if (!collection) {
    return [];
  }

  // Special handling for specific endpoints
  if (['dashboard', 'dashboards'].includes(parsed.resource)) {
    // If requesting a specific dashboard (default, etc.)
    if (endpoint.includes('/default')) {
      return null; // No default dashboard
    }
    // Return empty array for dashboard list
    return [];
  }

  // Notifications are handled in handleMockRequest before the method switch

  // Handle notification-catalog
  if (parsed.resource === 'notification-catalog') {
    if (endpoint.includes('queue/stats')) {
      return { pending: 0, processing: 0, sent_today: 0, failed_today: 0, skipped_today: 0 };
    }
    if (endpoint.includes('catalog')) {
      return { items: [], total: 0, categories: [] };
    }
    if (endpoint.includes('definitions')) {
      return [];
    }
    if (endpoint.includes('client-config')) {
      return [];
    }
    return [];
  }

  // Handle communications/notifications
  if (parsed.resource === 'communications') {
    if (parsed.id === 'notifications') {
      return [];
    }
    return [];
  }

  // Handle employees endpoint
  if (parsed.resource === 'employees') {
    return { employees: [] };
  }

  // Handle integrations endpoint
  if (parsed.resource === 'integrations') {
    if (parsed.id === 'jira') {
      if (parsed.subResource === 'status') {
        return { connected: false, workspace: null };
      }
      return { projects: [] };
    }
    return [];
  }

  // Handle aws environments (parsed as: resource='aws', id='environments'|'accounts'|'aivilo-account-id')
  if (parsed.resource === 'aws') {
    if (parsed.id === 'aivilo-account-id') {
      return { account_id: '111222333444' };
    }
    if (parsed.id === 'accounts') {
      const accounts = mockDataStore.get('awsAccounts');
      if (accounts && accounts.length > 0) return accounts;
      return [
        { id: 'acc-001', account_id: '123456789012', role_name: 'AiviloLensCrossAccountRole', account_type: 'standard', parent_payer_account_id: 'acc-003', created_at: '2025-06-15T10:00:00Z' },
        { id: 'acc-002', account_id: '987654321098', role_name: 'AiviloLensCrossAccountRole', account_type: 'standard', parent_payer_account_id: 'acc-003', created_at: '2025-06-15T10:00:00Z' },
        { id: 'acc-003', account_id: '555666777888', role_name: 'AiviloLensPayerRole', account_type: 'payer', parent_payer_account_id: null, created_at: '2025-06-01T10:00:00Z' },
      ];
    }
    if (parsed.id === 'environments') {
      const envs = mockDataStore.get('awsEnvironments');
      if (envs && envs.length > 0) return envs;
      return [
        {
          id: 'env-0000-0000-0000-000000000001', name: 'Production', description: 'Production workloads',
          environment_type: 'production', track_credits: true, aws_account_id: 'acc-0000-0000-0000-000000000001',
          aws_account: { id: 'acc-0000-0000-0000-000000000001', account_id: '123456789012', account_type: 'standard' },
          created_at: '2025-06-20T10:00:00Z',
        },
        {
          id: 'env-0000-0000-0000-000000000002', name: 'Staging', description: 'Staging environment',
          environment_type: 'staging', track_credits: false, aws_account_id: 'acc-0000-0000-0000-000000000001',
          aws_account: { id: 'acc-0000-0000-0000-000000000001', account_id: '123456789012', account_type: 'standard' },
          created_at: '2025-07-10T10:00:00Z',
        },
        {
          id: 'env-0000-0000-0000-000000000003', name: 'Development', description: 'Development environment',
          environment_type: 'development', track_credits: false, aws_account_id: 'acc-0000-0000-0000-000000000002',
          aws_account: { id: 'acc-0000-0000-0000-000000000002', account_id: '987654321098', account_type: 'standard' },
          created_at: '2025-06-20T10:00:00Z',
        },
        {
          id: 'env-0000-0000-0000-000000000004', name: 'QA', description: 'QA environment',
          environment_type: 'qa', track_credits: false, aws_account_id: 'acc-0000-0000-0000-000000000002',
          aws_account: { id: 'acc-0000-0000-0000-000000000002', account_id: '987654321098', account_type: 'standard' },
          created_at: '2025-07-15T10:00:00Z',
        },
      ];
    }
    return [];
  }

  // Handle support requests
  if (parsed.resource === 'support' && parsed.id === 'requests') {
    const tickets = mockDataStore.get('supportTickets') || [];
    return tickets;
  }

  // Handle tasks/executions
  if (parsed.resource === 'tasks') {
    return { executions: [], total: 0 };
  }

  // Handle funding endpoints (parsed as: resource='funding', id='sync'|'credits-summary'|etc.)
  if (parsed.resource === 'funding') {
    if (parsed.id === 'sync') {
      return { success: true, message: 'Funding data synced', synced: 3 };
    }
    if (parsed.id === 'credits-summary') {
      return {
        total_credits: 10000,
        total_credits_available: 5800,
        total_credits_allocated: 10000,
        used_credits: 4200,
        remaining_credits: 5800,
        active_credit_codes_count: 2,
        active_codes: 2,
        expired_codes: 1,
        redeemed_codes: 1,
        credit_codes: [
          { code: 'AWS-PC-2025-ALPHA', amount: 5000, aws_account_id: '123456789012', expiration_date: '2025-12-31' },
          { code: 'AWS-PC-2025-BETA', amount: 5000, aws_account_id: '123456789012', expiration_date: '2025-12-31' },
        ]
      };
    }
    if (parsed.id === 'credit-codes') {
      return [
        { id: 'cc-001', credit_code: 'AWS-PC-2025-ALPHA', amount: 5000, status: 'Active', aws_account_id: '123456789012', matched_client_name: 'TechCorp Industries', expiration_date: '2025-12-31' },
        { id: 'cc-002', credit_code: 'AWS-PC-2025-BETA', amount: 5000, status: 'Active', aws_account_id: '123456789012', matched_client_name: 'TechCorp Industries', expiration_date: '2025-12-31' },
        { id: 'cc-003', credit_code: 'AWS-PC-2024-GAMMA', amount: 2000, status: 'Redeemed', aws_account_id: '123456789012', matched_client_name: 'TechCorp Industries', expiration_date: '2024-12-31' },
        { id: 'cc-004', credit_code: 'AWS-PC-2024-DELTA', amount: 1500, status: 'Expired', aws_account_id: '123456789012', matched_client_name: null, expiration_date: '2024-06-30' }
      ];
    }
    if (parsed.id === 'applications') {
      if (parsed.subResource && parsed.fullPath.includes('allocations')) {
        return [
          { id: 'alloc-001', name: 'Q1 2025 AI Training Credits', status: 'Active', allocation_amount: 6500, issued_amount: 6500, starts_at: '2025-01-01', expiration_date: '2025-06-30' },
          { id: 'alloc-002', name: 'Q2 2025 Infrastructure Credits', status: 'Fulfilled', allocation_amount: 3500, issued_amount: 3500, starts_at: '2025-04-01', expiration_date: '2025-12-31' }
        ];
      }
      if (parsed.subResource && parsed.fullPath.includes('project')) {
        return { success: true };
      }
      if (parsed.subResource) {
        return {
          id: parsed.subResource, activity_name: 'AI Transformation Credits - TechCorp', program_name: 'AWS Partner Funding', status: 'APPROVED', stage: 'FULFILLMENT',
          fulfillment_types: 'Credit Code', budget_year: '2025', approved_amount: 10000, requested_amount: 10000, value_per_code: 5000, number_of_codes: 2,
          aws_account_id: '123456789012', planned_start_date: '2025-01-01', planned_end_date: '2025-12-31', business_description: 'AWS credits for AI/ML workload migration and GenAI training infrastructure.',
          application_id: 'APP-2025-001', submission_date: '2024-11-15', approval_date: '2024-12-01', project_id: 'p0000000-0000-4000-a000-000000000001', project_name: 'AI Transformation',
          calculator_urls: '[]', file_details: '[]'
        };
      }
      return [
        {
          id: 'app-001', activity_name: 'AI Transformation Credits - TechCorp', program_name: 'AWS Partner Funding', status: 'APPROVED', stage: 'FULFILLMENT',
          fulfillment_types: 'Credit Code', budget_year: '2025', approved_amount: 10000, requested_amount: 10000, value_per_code: 5000, number_of_codes: 2,
          aws_account_id: '123456789012', planned_start_date: '2025-01-01', planned_end_date: '2025-12-31', business_description: 'AWS credits for AI/ML workload migration.',
          application_id: 'APP-2025-001', submission_date: '2024-11-15', approval_date: '2024-12-01', project_id: 'p0000000-0000-4000-a000-000000000001', project_name: 'AI Transformation'
        },
        {
          id: 'app-002', activity_name: 'Cloud Migration Support - TechCorp', program_name: 'AWS Migration Acceleration', status: 'PENDING_SUBMISSION', stage: 'APPLICATION',
          fulfillment_types: 'Credit Code', budget_year: '2025', approved_amount: 0, requested_amount: 7500, value_per_code: 0, number_of_codes: 0,
          aws_account_id: '123456789012', planned_start_date: '2025-03-01', planned_end_date: '2025-09-30', business_description: 'Credits for legacy workload migration to AWS.',
          application_id: 'APP-2025-002', submission_date: null, approval_date: null, project_id: null, project_name: null
        }
      ];
    }
    if (parsed.id === 'allocations' && parsed.subResource) {
      // /funding/allocations/:id/credit-codes
      return [
        { id: 'cc-001', credit_code: 'AWS-PC-2025-ALPHA', amount: 5000, status: 'Active', aws_account_id: '123456789012', matched_client_name: 'TechCorp Industries', expiration_date: '2025-12-31' },
        { id: 'cc-002', credit_code: 'AWS-PC-2025-BETA', amount: 5000, status: 'Active', aws_account_id: '123456789012', matched_client_name: 'TechCorp Industries', expiration_date: '2025-12-31' }
      ];
    }
    return [];
  }

  // Handle project sub-resources (funding-requests, people, milestones, environments, cost-config, sow)
  if (parsed.resource === 'projects' && parsed.id && parsed.subResource) {
    if (parsed.subResource === 'workloads') {
      const workloads = mockDataStore.get('workloads') || [];
      return workloads.filter((w: Record<string, unknown>) => {
        const projectIds = (w.project_ids as string[]) || [];
        return projectIds.includes(parsed.id as string);
      });
    }

    if (parsed.subResource === 'funding-requests') {
      return [
        {
          id: 'app-001', name: 'AI Transformation Credits', program_name: 'AWS Partner Funding', status: 'APPROVED',
          requested_amount: 10000, approved_amount: 10000, submission_date: '2024-11-15', approval_date: '2024-12-01'
        }
      ];
    }

    if (parsed.subResource === 'people') {
      return {
        people: [
          { id: 'u0000000-0000-4000-a000-000000000001', name: 'Sarah Chen', email: 'sarah.chen@techcorp.com', role: 'Tech Lead', hourlyRate: 145, partnerName: 'Aivilo' },
          { id: 'u0000000-0000-4000-a000-000000000002', name: 'Marcus Rodriguez', email: 'marcus.r@techcorp.com', role: 'Cloud Architect', hourlyRate: 160, partnerName: 'Aivilo' },
          { id: 'u0000000-0000-4000-a000-000000000003', name: 'Emily Watson', email: 'emily.w@techcorp.com', role: 'Data Engineer', hourlyRate: 125, partnerName: 'Aivilo' },
          { id: 'u0000000-0000-4000-a000-000000000004', name: 'James Kim', email: 'james.k@techcorp.com', role: 'ML Engineer', hourlyRate: 135, partnerName: 'Aivilo' }
        ]
      };
    }

    if (parsed.subResource === 'milestones') {
      const milestones = mockDataStore.get('milestones') || [];
      return milestones.filter((m: Record<string, unknown>) => m.project_id === parsed.id);
    }

    if (parsed.subResource === 'environments') {
      return [
        {
          id: 'e0000000-0000-4000-a000-000000000001', name: 'Production',
          description: 'Production workloads - AI/ML inference and APIs',
          environment_type: 'production', aws_account_id: 'acc-001',
          aws_account: { id: 'acc-001', account_id: '123456789012', account_type: 'standard' },
          created_at: '2025-06-20T10:00:00Z'
        }
      ];
    }

    if (parsed.subResource === 'cost-config') {
      return {
        cost_tracking_enabled: true,
        cost_tag_key: 'Project',
        cost_tag_value: 'ai-transformation-initiative'
      };
    }

    if (parsed.subResource === 'sow') {
      if (endpoint.includes('/download-url')) {
        return { download_url: '#' };
      }
      if (endpoint.includes('/google-drive/files')) {
        return {
          files: [
            { id: 'gdoc-001', name: 'AI Transformation - Statement of Work v2.1.pdf', size: 2457600, mimeType: 'application/pdf', downloadLink: '#', viewLink: '#' },
            { id: 'gdoc-002', name: 'Project Requirements - Appendix A.docx', size: 524288, mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', downloadLink: '#', viewLink: '#' }
          ]
        };
      }
      return { success: true };
    }

    // Fallback for other project sub-resources
    return [];
  }

  // Handle finance/analytics
  if (parsed.resource === 'finance') {
    if (parsed.id === 'analytics') {
      const projects = mockDataStore.get('projects') || [];
      const totalBudget = projects.reduce((sum: number, p: Record<string, unknown>) => sum + ((p.budget as number) || 0), 0);
      const totalSpent = projects.reduce((sum: number, p: Record<string, unknown>) => sum + ((p.spent as number) || (p.total_spent as number) || 0), 0);
      const burnRate = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

      return {
        summary: {
          total_budget: totalBudget,
          estimated_spend: totalSpent,
          remaining_budget: totalBudget - totalSpent,
          burn_rate: burnRate,
        },
        monthly_breakdown: Array.from({ length: 12 }, (_, i) => {
          const weights = [0.07, 0.07, 0.07, 0.07, 0.07, 0.08, 0.08, 0.08, 0.09, 0.1, 0.11, 0.11];
          return {
            month: new Date(Date.now() - (11 - i) * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 7),
            spend: Math.round(totalSpent * weights[i])
          };
        })
      };
    }
    if (parsed.id === 'budget-alerts') {
      return { alerts: [] };
    }
    return mockDataStore.get('costs') || [];
  }

  // Handle timesheets/hours-by-person
  if (parsed.resource === 'timesheets') {
    if (parsed.id === 'hours-by-person') {
      const timesheets = mockDataStore.get('timesheets') || [];
      // Aggregate hours by person - return shape expected by getHoursByPerson: { items: [{name, hours}], total_hours }
      const hoursByPerson: Record<string, number> = {};
      let totalHours = 0;
      timesheets.forEach((ts: Record<string, unknown>) => {
        const name = (ts.user_name as string) || (ts.employee_name as string) || 'Unknown';
        if (!hoursByPerson[name]) hoursByPerson[name] = 0;
        hoursByPerson[name] += (ts.hours as number) || 0;
        totalHours += (ts.hours as number) || 0;
      });
      return {
        items: Object.entries(hoursByPerson).map(([name, hours]) => ({ name, hours })),
        total_hours: totalHours
      };
    }
    return mockDataStore.get('timesheets') || [];
  }

  // Handle workloads sub-resources (tags/keys, tags/values)
  if (parsed.resource === 'workloads' && parsed.id === 'tags') {
    if (parsed.subResource === 'keys') {
      return ['Environment', 'Product', 'Team', 'CostCenter', 'Application', 'Owner'];
    }
    if (parsed.subResource === 'values') {
      const urlParams = new URLSearchParams(endpoint.split('?')[1] || '');
      const key = urlParams.get('key') || '';
      const valuesMap: Record<string, string[]> = {
        'Environment': ['production', 'staging', 'development', 'qa', 'sandbox'],
        'Product': ['ai-platform', 'data-pipeline', 'web-app', 'api-gateway', 'analytics'],
        'Team': ['platform', 'data-engineering', 'ml-ops', 'devops', 'security'],
        'CostCenter': ['engineering', 'operations', 'research', 'marketing'],
        'Application': ['customer-portal', 'internal-tools', 'ml-inference', 'etl-jobs'],
        'Owner': ['team-alpha', 'team-beta', 'team-gamma'],
      };
      return valuesMap[key] || [];
    }
  }

  // Handle finops/workload-costs
  if (parsed.resource === 'finops' && parsed.id === 'workload-costs') {
    const workloads = mockDataStore.get('workloads') || [];
    const workloadCostData: Record<string, { total_spent: number; trend_pct: number; monthly_breakdown: { month: string; amount: number }[] }> = {
      'w0000000-0000-4000-a000-000000000001': {
        total_spent: 2450.00,
        trend_pct: 5.2,
        monthly_breakdown: [
          { month: '2026-01', amount: 780.00 },
          { month: '2026-02', amount: 820.00 },
          { month: '2026-03', amount: 850.00 },
        ],
      },
      'w0000000-0000-4000-a000-000000000002': {
        total_spent: 1320.50,
        trend_pct: -3.8,
        monthly_breakdown: [
          { month: '2026-01', amount: 460.00 },
          { month: '2026-02', amount: 450.50 },
          { month: '2026-03', amount: 410.00 },
        ],
      },
      'w0000000-0000-4000-a000-000000000003': {
        total_spent: 3180.75,
        trend_pct: 12.1,
        monthly_breakdown: [
          { month: '2026-01', amount: 920.00 },
          { month: '2026-02', amount: 1050.25 },
          { month: '2026-03', amount: 1210.50 },
        ],
      },
    };
    return workloads.map((w: Record<string, unknown>) => {
      const costData = workloadCostData[w.id as string] || {
        total_spent: 0,
        trend_pct: 0,
        monthly_breakdown: [],
      };
      return {
        workload_id: w.id,
        workload_name: w.name,
        total_spent: costData.total_spent,
        trend_pct: costData.trend_pct,
        project_names: w.project_names || [],
        monthly_breakdown: costData.monthly_breakdown,
      };
    });
  }

  // Handle finops/anomalies - import from cloudops mock data
  if (parsed.resource === 'finops' && parsed.id === 'anomalies') {
    // eslint-disable-next-line global-require
    const { getCloudOpsMockData } = require('./mockData');
    return getCloudOpsMockData(endpoint);
  }

  // Handle finops/project-costs
  if (parsed.resource === 'finops' && parsed.id === 'project-costs') {
    let selectedProjectId: string | null = null;
    try {
      selectedProjectId = localStorage.getItem('selectedProjectId');
    } catch {
      selectedProjectId = null;
    }
    const projects = mockDataStore.get('projects') || [];
    const filteredProjects = selectedProjectId
      ? projects.filter((p: Record<string, unknown>) => p.id === selectedProjectId)
      : projects;
    return filteredProjects.map((p: Record<string, unknown>) => ({
      project_id: p.id,
      project_name: p.name,
      client_name: p.client_name || 'TechCorp Industries',
      infrastructure_budget: (p.budget as number) || 0,
      infrastructure_spent: (p.spent as number) || (p.total_spent as number) || 0,
      infrastructure_remaining: ((p.budget as number) || 0) - ((p.spent as number) || (p.total_spent as number) || 0),
      cost_tracking_enabled: true,
      status: (((p.spent as number) || (p.total_spent as number) || 0) / ((p.budget as number) || 1)) > 0.9 ? 'over_budget'
            : (((p.spent as number) || (p.total_spent as number) || 0) / ((p.budget as number) || 1)) > 0.7 ? 'warning' : 'on_track'
    }));
  }

  if (parsed.resource === 'finops' && endpoint.includes('summary')) {
    const costs = mockDataStore.get('costs');
    return costs.summary || costs;
  }

  if (parsed.resource === 'tagging' && endpoint.includes('compliance')) {
    return mockDataStore.get('tagging');
  }

  if (parsed.resource === 'guardrails' && endpoint.includes('summary')) {
    const guardrails = mockDataStore.get('guardrails');
    return guardrails.summary || guardrails;
  }

  // Get projects list - enrich each with milestones
  if (parsed.resource === 'projects' && !parsed.id) {
    const projects = mockDataStore.get('projects') || [];
    const allMilestones = mockDataStore.get('milestones') || [];
    return projects.map((p: Record<string, unknown>) => ({
      ...p,
      milestones: allMilestones.filter((m: Record<string, unknown>) => m.project_id === p.id)
    }));
  }

  // Get single project by ID - enrich with milestones
  if (parsed.resource === 'projects' && parsed.id && !parsed.subResource) {
    const project = mockDataStore.getById(collection, parsed.id);
    if (!project) {
      return null;
    }
    // Enrich with milestones from milestones collection
    const allMilestones = mockDataStore.get('milestones') || [];
    const projectMilestones = allMilestones.filter((m: Record<string, unknown>) => m.project_id === parsed.id);
    return { ...project, milestones: projectMilestones };
  }

  // Get by ID
  if (parsed.id) {
    const item = mockDataStore.getById(collection, parsed.id);
    if (!item) {
      // Return null instead of throwing error for missing items
      return null;
    }
    return item;
  }

  // Get collection (with potential filters from query params)
  const items = mockDataStore.get(collection);

  // Handle query parameters for filtering
  const url = new URL(endpoint, 'http://dummy.com');
  const params = url.searchParams;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let filtered: any = items;

  if (params.has('project_id')) {
    const projectId = params.get('project_id');
    filtered = Array.isArray(filtered) ? filtered.filter((item: Record<string, unknown>) => item.project_id === projectId) : filtered;
  }

  if (params.has('workload_id')) {
    const workloadId = params.get('workload_id');
    filtered = Array.isArray(filtered) ? filtered.filter((item: Record<string, unknown>) => item.workload_id === workloadId) : filtered;
  }

  if (params.has('account_id')) {
    const accountId = params.get('account_id');
    filtered = Array.isArray(filtered) ? filtered.filter((item: Record<string, unknown>) => item.account_id === accountId || item.cloud_account_id === accountId) : filtered;
  }

  if (params.has('environment_id')) {
    const environmentId = params.get('environment_id');
    filtered = Array.isArray(filtered) ? filtered.filter((item: Record<string, unknown>) => item.environment_id === environmentId) : filtered;
  }

  if (params.has('status')) {
    const status = params.get('status');
    filtered = Array.isArray(filtered) ? filtered.filter((item: Record<string, unknown>) => item.status === status) : filtered;
  }

  // client_id: in demo mode, return all items regardless
  return filtered;
};

/**
 * Handle POST requests (create)
 */
const handlePost = (
  mockDataStore: MockDataStore,
  parsed: ParsedEndpoint,
  collection: string,
  options: ApiCallOptions
): unknown => {
  // Handle assessment POST endpoints
  if (parsed.resource === 'assessments') {
    // POST /assessments/draft - create new draft
    if (parsed.id === 'draft' && !parsed.subResource) {
      const newDraft = {
        id: 'as-demo-' + Date.now(),
        status: 'draft',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        completed_at: null,
        client_name: 'TechCorp Industries',
        client_id: 'c0000000-0000-4000-a000-000000000001',
        owner_email: 'demo@example.com',
        overall_score: null,
        readiness_level: null,
      };
      return newDraft;
    }

    // POST /assessments/:id/submit - submit a draft
    if (parsed.id && parsed.subResource === 'submit') {
      return {
        ...mockAssessmentResults,
        id: parsed.id,
        status: 'completed',
        assessment_id: parsed.id,
        overall_score: 72,
        readiness_level: 'Moderate',
      };
    }

    // POST /assessments - submit new assessment
    const newId = 'as-demo-' + Date.now();
    return {
      ...mockAssessmentResults,
      id: newId,
      status: 'processing',
      assessment_id: newId,
    };
  }

  // Handle assessment-results POST
  if (parsed.resource === 'assessment-results') {
    return mockAssessmentResults;
  }

  if (!collection) {
    return { success: true, id: 'demo-' + Date.now() };
  }

  const body = options.body ? JSON.parse(options.body as string) : {};
  const newItem = mockDataStore.create(collection, body);

  return newItem;
};

/**
 * Handle PUT/PATCH requests (update)
 */
const handleUpdate = (
  mockDataStore: MockDataStore,
  parsed: ParsedEndpoint,
  collection: string,
  options: ApiCallOptions
): unknown => {
  // Handle assessment draft save: PUT /assessments/:id/draft
  if (parsed.resource === 'assessments' && parsed.id && parsed.subResource === 'draft') {
    const body = options.body ? JSON.parse(options.body as string) : {};
    localStorage.setItem(`demo_draft_${parsed.id}`, JSON.stringify(body.responses || {}));
    return { success: true, responses: body.responses };
  }

  // Handle project cost-config update
  if (parsed.resource === 'projects' && parsed.subResource === 'cost-config') {
    const body = options.body ? JSON.parse(options.body as string) : {};
    mockDataStore.update('projects', parsed.id as string, body);
    return body;
  }

  if (!collection || !parsed.id) {
    const error = Object.assign(new Error('Invalid update request'), { status: 400 });
    throw error;
  }

  const body = options.body ? JSON.parse(options.body as string) : {};
  const updatedItem = mockDataStore.update(collection, parsed.id, body);

  if (!updatedItem) {
    const error = Object.assign(new Error('Not found'), { status: 404 });
    throw error;
  }

  return updatedItem;
};

/**
 * Handle DELETE requests
 */
const handleDelete = (
  mockDataStore: MockDataStore,
  parsed: ParsedEndpoint,
  collection: string
): null => {
  if (!collection || !parsed.id) {
    const error = Object.assign(new Error('Invalid delete request'), { status: 400 });
    throw error;
  }

  const removed = mockDataStore.remove(collection, parsed.id);

  if (!removed) {
    const error = Object.assign(new Error('Not found'), { status: 404 });
    throw error;
  }

  // Return null for successful DELETE (204 No Content)
  return null;
};

export default createDemoApiWrapper;
